import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaTrophy, FaClock, FaBolt, FaRedo, FaCrown, FaStar } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

const TOTAL_QUESTIONS = 10;
const TIME_PER_QUESTION = 15;
const MAX_SCORE_PER_Q = 100;
const MIN_SCORE_PER_Q = 10;

const REACTIONS = {
  correct: ['🎉', '🔥', '💪', '⚡', '🚀', '✨', '🌟', '👏'],
  wrong: ['😅', '💀', '🥲', '😬', '🫠', '😵', '🤯', '😢'],
  streak: ['🔥🔥🔥', '⚡ON FIRE⚡', '💎 UNSTOPPABLE', '🌟 LEGEND'],
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Map DB row → game format
const mapDbQuestion = (row) => {
  const opts = [row.option_a, row.option_b, row.option_c, row.option_d];
  const answerMap = { a: 0, b: 1, c: 2, d: 3 };
  return { q: row.question, options: opts, answer: answerMap[row.correct_option] || 0 };
};

const PimnasQuiz = () => {
  const { t } = useLanguage();

  const [phase, setPhase] = useState('start');
  const [playerName, setPlayerName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_PER_QUESTION);
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [reaction, setReaction] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [shakeWrong, setShakeWrong] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const fetchLeaderboard = useCallback(async () => {
    const { data } = await supabase
      .from('quiz_scores')
      .select('*')
      .order('score', { ascending: false })
      .order('time_seconds', { ascending: true })
      .limit(20);
    setLeaderboard(data || []);
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Timer
  useEffect(() => {
    if (phase !== 'playing' || showAnswer) return;
    if (timeLeft <= 0) { handleAnswer(null); return; }
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, phase, showAnswer]);

  const startGame = async () => {
    if (!playerName.trim()) return;
    setLoadingQuestions(true);
    const { data } = await supabase.from('quiz_questions').select('*');
    setLoadingQuestions(false);
    if (!data || data.length === 0) return;
    const mapped = data.map(mapDbQuestion);
    const picked = shuffle(mapped).slice(0, Math.min(TOTAL_QUESTIONS, mapped.length));
    setQuestions(picked);
    setCurrentQ(0);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setCorrectCount(0);
    setTimeLeft(TIME_PER_QUESTION);
    setSelected(null);
    setShowAnswer(false);
    setTotalTime(0);
    startTimeRef.current = Date.now();
    setPhase('playing');
  };

  const handleAnswer = (idx) => {
    if (showAnswer) return;
    clearTimeout(timerRef.current);
    setSelected(idx);
    setShowAnswer(true);

    const q = questions[currentQ];
    const isCorrect = idx === q.answer;

    if (isCorrect) {
      const timeBonus = Math.round((timeLeft / TIME_PER_QUESTION) * (MAX_SCORE_PER_Q - MIN_SCORE_PER_Q));
      const pts = MIN_SCORE_PER_Q + timeBonus;
      const newStreak = streak + 1;
      const streakBonus = newStreak >= 5 ? 50 : newStreak >= 3 ? 25 : 0;
      setScore(s => s + pts + streakBonus);
      setStreak(newStreak);
      setMaxStreak(m => Math.max(m, newStreak));
      setCorrectCount(c => c + 1);
      setReaction(REACTIONS.correct[Math.floor(Math.random() * REACTIONS.correct.length)]);
    } else {
      setStreak(0);
      setShakeWrong(true);
      setTimeout(() => setShakeWrong(false), 500);
      setReaction(REACTIONS.wrong[Math.floor(Math.random() * REACTIONS.wrong.length)]);
    }

    setTimeout(() => {
      if (currentQ + 1 >= questions.length) {
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
        setTotalTime(elapsed);
        const finalScore = score + (isCorrect ? MIN_SCORE_PER_Q + Math.round((timeLeft / TIME_PER_QUESTION) * (MAX_SCORE_PER_Q - MIN_SCORE_PER_Q)) + (streak + 1 >= 5 ? 50 : streak + 1 >= 3 ? 25 : 0) : 0);
        finishGame(finalScore, elapsed, correctCount + (isCorrect ? 1 : 0));
      } else {
        setCurrentQ(c => c + 1);
        setTimeLeft(TIME_PER_QUESTION);
        setSelected(null);
        setShowAnswer(false);
        setReaction('');
      }
    }, 1500);
  };

  const finishGame = async (finalScore, elapsed, correct) => {
    setPhase('result');
    await supabase.from('quiz_scores').insert({
      player_name: playerName.trim(),
      score: finalScore,
      total_questions: questions.length,
      correct_answers: correct,
      time_seconds: elapsed,
    });
    fetchLeaderboard();
  };

  const playAgain = () => {
    setPhase('start');
    setReaction('');
  };

  // ──────── START SCREEN ────────
  if (phase === 'start') {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">🧠</div>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1">{t('quiz.title')}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('quiz.subtitle')}</p>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-gold-50 dark:from-primary-900/30 dark:to-gold-900/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FaBolt className="text-gold-500" /> {t('quiz.howToPlay')}
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li className="flex items-start gap-2"><span>🎯</span> {t('quiz.rule1')}</li>
              <li className="flex items-start gap-2"><span>⏱️</span> {t('quiz.rule2')}</li>
              <li className="flex items-start gap-2"><span>⚡</span> {t('quiz.rule3')}</li>
              <li className="flex items-start gap-2"><span>🔥</span> {t('quiz.rule4')}</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t('quiz.yourName')}</label>
            <input
              type="text"
              value={playerName}
              onChange={e => setPlayerName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && startGame()}
              placeholder={t('quiz.namePlaceholder')}
              maxLength={30}
              className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-sm"
            />
          </div>

          <button
            onClick={startGame}
            disabled={!playerName.trim() || loadingQuestions}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-40 text-lg flex items-center justify-center gap-2"
          >
            {loadingQuestions ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🚀'} {t('quiz.startGame')}
          </button>
        </div>

        {leaderboard.length > 0 && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaTrophy className="text-gold-500" /> {t('quiz.leaderboard')}
            </h3>
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((entry, i) => (
                <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl ${i < 3 ? 'bg-gradient-to-r from-gold-50 to-amber-50 dark:from-gold-900/20 dark:to-amber-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                    backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                    color: i < 3 ? 'white' : '#6b7280',
                  }}>
                    {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex-1 truncate">{entry.player_name}</span>
                  <span className="text-sm font-bold text-primary-600 dark:text-primary-400">{entry.score}</span>
                  <span className="text-xs text-gray-400">{entry.time_seconds}s</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──────── PLAYING ────────
  if (phase === 'playing') {
    const q = questions[currentQ];
    const progress = ((currentQ) / questions.length) * 100;
    const timerPct = (timeLeft / TIME_PER_QUESTION) * 100;
    const timerColor = timeLeft <= 3 ? 'bg-red-500' : timeLeft <= 7 ? 'bg-amber-500' : 'bg-emerald-500';

    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {currentQ + 1} / {questions.length}
            </span>
            <div className="flex items-center gap-3">
              {streak >= 3 && (
                <span className="text-xs font-bold text-orange-500 animate-pulse">
                  {REACTIONS.streak[Math.min(streak - 3, REACTIONS.streak.length - 1)]}
                </span>
              )}
              <span className="flex items-center gap-1 text-sm font-bold text-primary-600 dark:text-primary-400">
                <FaStar className="text-gold-500" /> {score}
              </span>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary-500 to-gold-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <FaClock className={`text-sm ${timeLeft <= 3 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`} />
          <div className="flex-1 h-2 bg-gray-100 dark:bg-dark-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-1000 ${timerColor}`} style={{ width: `${timerPct}%` }} />
          </div>
          <span className={`text-sm font-bold min-w-[2rem] text-right ${timeLeft <= 3 ? 'text-red-500 animate-pulse' : 'text-gray-500 dark:text-gray-400'}`}>
            {timeLeft}s
          </span>
        </div>

            <div className={`bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8 mb-6 ${shakeWrong ? 'animate-shake' : ''}`}>
              {reaction && showAnswer && (
                <div className="text-center text-4xl mb-4 animate-bounce-in">{reaction}</div>
              )}

              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white text-center leading-relaxed mb-8">
                {q.q}
              </h2>

              <div className="grid grid-cols-1 gap-3">
                {q.options.map((opt, i) => {
                  const isCorrectOpt = i === q.answer;
                  const isSelected = selected === i;
                  let optClass = 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:border-primary-300 cursor-pointer';

                  if (showAnswer) {
                    if (isCorrectOpt) optClass = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-400';
                    else if (isSelected && !isCorrectOpt) optClass = 'border-red-400 bg-red-50 dark:bg-red-900/30 ring-2 ring-red-400';
                    else optClass = 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 opacity-50';
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => handleAnswer(i)}
                      disabled={showAnswer}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all font-medium text-sm sm:text-base ${optClass}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          showAnswer && isCorrectOpt ? 'bg-emerald-500 text-white'
                            : showAnswer && isSelected && !isCorrectOpt ? 'bg-red-500 text-white'
                            : 'bg-gray-200 dark:bg-dark-600 text-gray-600 dark:text-gray-300'
                        }`}>
                          {showAnswer && isCorrectOpt ? '✓' : showAnswer && isSelected && !isCorrectOpt ? '✗' : String.fromCharCode(65 + i)}
                        </span>
                        <span className="text-gray-800 dark:text-gray-200">{opt}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {showAnswer && selected === null && (
                <p className="text-center text-red-500 font-bold mt-4 animate-pulse">⏰ {t('quiz.timesUp')}</p>
              )}
            </div>

        <style>{`
          @keyframes shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-6px); } 40%,80% { transform: translateX(6px); } }
          .animate-shake { animation: shake 0.4s ease; }
          @keyframes bounceIn { 0% { transform: scale(0); } 60% { transform: scale(1.3); } 100% { transform: scale(1); } }
          .animate-bounce-in { animation: bounceIn 0.4s ease; }
        `}</style>
      </div>
    );
  }

  // ──────── RESULT ────────
  const maxPossible = questions.length * MAX_SCORE_PER_Q;
  const pct = maxPossible > 0 ? Math.round((score / maxPossible) * 100) : 0;
  const grade = pct >= 90 ? { emoji: '🏆', label: t('quiz.gradeLegend'), color: 'from-yellow-400 to-amber-500' }
    : pct >= 70 ? { emoji: '🌟', label: t('quiz.gradeExcellent'), color: 'from-primary-500 to-indigo-500' }
    : pct >= 50 ? { emoji: '👍', label: t('quiz.gradeGood'), color: 'from-emerald-500 to-teal-500' }
    : pct >= 30 ? { emoji: '📚', label: t('quiz.gradeKeepLearning'), color: 'from-blue-500 to-cyan-500' }
    : { emoji: '💪', label: t('quiz.gradeTryAgain'), color: 'from-gray-500 to-gray-600' };

  const playerRank = leaderboard.findIndex(e => e.score <= score);
  const rank = playerRank === -1 ? leaderboard.length + 1 : playerRank + 1;

  return (
    <div className="space-y-6">
      {/* Result Header */}
      <div className={`bg-gradient-to-r ${grade.color} rounded-2xl p-8 text-center`}>
        <div className="text-6xl mb-3">{grade.emoji}</div>
        <h2 className="text-2xl font-extrabold text-white mb-1">{grade.label}</h2>
        <p className="text-white/80">{playerName}</p>
      </div>

      {/* Score Card */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-100 dark:border-dark-700 p-6 sm:p-8 text-center">
        <div className="text-5xl font-extrabold text-gray-900 dark:text-white mb-1">{score}</div>
        <p className="text-gray-400 text-sm mb-6">{t('quiz.points')}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
              <p className="text-2xl font-bold text-emerald-600">{correctCount}/{questions.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.correct')}</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
              <p className="text-2xl font-bold text-amber-600">{maxStreak} 🔥</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.bestStreak')}</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
              <p className="text-2xl font-bold text-blue-600">{totalTime}s</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.time')}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
              <p className="text-2xl font-bold text-purple-600">#{rank}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('quiz.rank')}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={playAgain} className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2">
              <FaRedo /> {t('quiz.playAgain')}
            </button>
          </div>
        </div>

        {leaderboard.length > 0 && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaCrown className="text-gold-500" /> {t('quiz.globalLeaderboard')}
            </h3>
            <div className="space-y-2">
              {leaderboard.map((entry, i) => {
                const isYou = entry.player_name === playerName.trim() && entry.score === score;
                return (
                  <div key={entry.id} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${isYou ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-400' : i < 3 ? 'bg-gradient-to-r from-gold-50 to-amber-50 dark:from-gold-900/20 dark:to-amber-900/20' : 'bg-gray-50 dark:bg-dark-700'}`}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{
                      backgroundColor: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#e5e7eb',
                      color: i < 3 ? 'white' : '#6b7280',
                    }}>
                      {i < 3 ? <>{i === 0 ? '👑' : i === 1 ? '🥈' : '🥉'}</> : i + 1}
                    </span>
                    <span className={`text-sm font-medium flex-1 truncate ${isYou ? 'text-primary-700 dark:text-primary-400 font-bold' : 'text-gray-700 dark:text-gray-200'}`}>
                      {entry.player_name} {isYou && <span className="text-xs">({t('quiz.you')})</span>}
                    </span>
                    <span className="text-xs text-gray-400">{entry.correct_answers}/{entry.total_questions}</span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">{entry.score}</span>
                    <span className="text-xs text-gray-400">{entry.time_seconds}s</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
};

export default PimnasQuiz;
